import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Plus, Search, Edit, Trash2, Users, X, Award, Calendar, DollarSign,
  Mail, Phone, MessageSquare, TrendingUp, Clock, MapPin, Star,
  CheckCircle, Gift, History, Eye, Send
} from 'lucide-react'
import { customerAPI, bookingsAPI } from '../lib/api'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Textarea } from '../components/ui/Textarea'
import { useToast } from '../components/ui/useToast'
import type { Customer } from '../types'

export function CustomersPageNew() {
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null)
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(null)
  const [viewingCustomer, setViewingCustomer] = useState<Customer | null>(null)
  const { notify } = useToast()

  const {
    data: customersResponse,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['customers', { search: searchTerm, page }],
    queryFn: () => customerAPI.getAll({
      search: searchTerm || undefined,
      page,
      per_page: 25
    })
  })

  const customersData = (customersResponse as any)?.data || {}
  const customers = Array.isArray(customersData.items) ? customersData.items : []
  const total = typeof customersData.total === 'number' ? customersData.total : 0
  const totalPages = typeof customersData.pages === 'number' ? customersData.pages : 0

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setPage(1)
  }

  const handleDelete = async () => {
    if (!deletingCustomer) return
    try {
      const res = await customerAPI.remove(deletingCustomer.id)
      if (res.success) {
        notify({ kind: 'success', message: 'Customer deleted successfully' })
        setDeletingCustomer(null)
        refetch()
      } else {
        notify({ kind: 'error', message: res.message || 'Failed to delete customer' })
      }
    } catch (err) {
      notify({ kind: 'error', message: 'Failed to delete customer' })
    }
  }

  // Calculate stats
  const stats = {
    total: total,
    active: customers.filter((c: any) => c.loyalty_points > 0 || c.total_bookings > 0).length,
    avgLoyalty: customers.length > 0
      ? Math.round(customers.reduce((sum: number, c: any) => sum + (c.loyalty_points || 0), 0) / customers.length)
      : 0
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 dark:text-red-400 mb-4">
          Failed to load customers. Please try again.
        </div>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Customers Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage customer profiles, loyalty, and history
          </p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Add Customer
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/5 dark:to-indigo-500/5 border-blue-200 dark:border-blue-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Customers</p>
                <p className="text-3xl font-bold mt-2 text-blue-900 dark:text-blue-100">{stats.total}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
                <Users className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/5 dark:to-emerald-500/5 border-green-200 dark:border-green-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Active Customers</p>
                <p className="text-3xl font-bold mt-2 text-green-900 dark:text-green-100">{stats.active}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-500/5 dark:to-pink-500/5 border-purple-200 dark:border-purple-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Avg Loyalty Points</p>
                <p className="text-3xl font-bold mt-2 text-purple-900 dark:text-purple-100">{stats.avgLoyalty}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                <Award className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="card-body">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search customers by name or email..."
              className="pl-10"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>
      </div>

      {/* Customers Grid */}
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <Users className="h-5 w-5 text-orange-600" />
          <h2 className="font-semibold text-lg">All Customers ({total})</h2>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl animate-pulse">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                </div>
              ))}
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No customers found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchTerm ? 'No customers match your search.' : 'Get started by adding your first customer.'}
              </p>
              <Button onClick={() => setShowModal(true)} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                <Plus className="h-4 w-4 mr-2" />
                Add Customer
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customers.map((customer: any) => (
                <div
                  key={customer.id}
                  className="p-4 rounded-xl border-2 border-gray-200 dark:border-gray-800 hover:border-orange-500 dark:hover:border-orange-500 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-lg">
                        {customer.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{customer.name}</h3>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Star className="h-3 w-3 text-orange-500" />
                          {customer.loyalty_points || 0} points
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{customer.email}</span>
                    </div>
                    {customer.phone && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Phone className="h-3 w-3" />
                        {customer.phone}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Calendar className="h-3 w-3" />
                      Joined {new Date(customer.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                    </div>
                  </div>

                  {customer.notes && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 italic mb-3 line-clamp-2">
                      {customer.notes}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setViewingCustomer(customer)}
                      className="flex-1 text-xs"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingCustomer(customer)}
                      className="text-xs"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeletingCustomer(customer)}
                      className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 text-xs"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Showing {((page - 1) * 25) + 1} to {Math.min(page * 25, total)} of {total} results
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <AddCustomerModal
          onClose={() => setShowModal(false)}
          onCreated={() => {
            notify({ kind: 'success', message: 'Customer added successfully' })
            setShowModal(false)
            refetch()
          }}
        />
      )}

      {editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onUpdated={() => {
            notify({ kind: 'success', message: 'Customer updated successfully' })
            setEditingCustomer(null)
            refetch()
          }}
        />
      )}

      {viewingCustomer && (
        <CustomerDetailModal
          customer={viewingCustomer}
          onClose={() => setViewingCustomer(null)}
          onEdit={() => {
            setEditingCustomer(viewingCustomer)
            setViewingCustomer(null)
          }}
        />
      )}

      {deletingCustomer && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-xl font-bold">Delete Customer</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to delete <span className="font-semibold">{deletingCustomer.name}</span>? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => setDeletingCustomer(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function AddCustomerModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const setField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Name is required'
    if (!form.email.trim()) e.email = 'Email is required'
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = 'Email is invalid'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      await customerAPI.create({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || undefined,
        notes: form.notes || undefined
      })
      onCreated()
    } catch (err: any) {
      const backendErrors = err?.response?.data?.errors
      if (backendErrors && typeof backendErrors === 'object') {
        const mapped: Record<string, string> = {}
        for (const key of Object.keys(backendErrors)) {
          const val = backendErrors[key]
          mapped[key] = Array.isArray(val) ? String(val[0]) : String(val)
        }
        setErrors(mapped)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 px-6 py-5 border-b border-gray-200 dark:border-gray-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Add Customer</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Create a new customer profile</p>
              </div>
            </div>
            <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="form-label flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              Full name *
            </label>
            <Input name="name" value={form.name} onChange={setField} placeholder="e.g., John Doe" required />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              Email *
            </label>
            <Input name="email" type="email" value={form.email} onChange={setField} placeholder="name@example.com" required />
            {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              Phone
            </label>
            <Input name="phone" value={form.phone} onChange={setField} placeholder="Optional" />
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-gray-500" />
              Notes
            </label>
            <Textarea name="notes" value={form.notes} onChange={setField} rows={3} placeholder="Any relevant details..." />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              {submitting ? 'Creating...' : 'Create Customer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditCustomerModal({ customer, onClose, onUpdated }: { customer: Customer; onClose: () => void; onUpdated: () => void }) {
  const [form, setForm] = useState({
    name: customer.name,
    email: customer.email,
    phone: customer.phone || '',
    notes: customer.notes || ''
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const setField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Name is required'
    if (!form.email.trim()) e.email = 'Email is required'
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = 'Email is invalid'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      await customerAPI.update(customer.id, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || undefined,
        notes: form.notes || undefined
      })
      onUpdated()
    } catch (err: any) {
      const backendErrors = err?.response?.data?.errors
      if (backendErrors && typeof backendErrors === 'object') {
        const mapped: Record<string, string> = {}
        for (const key of Object.keys(backendErrors)) {
          const val = backendErrors[key]
          mapped[key] = Array.isArray(val) ? String(val[0]) : String(val)
        }
        setErrors(mapped)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 px-6 py-5 border-b border-gray-200 dark:border-gray-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Edit className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Edit Customer</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Update customer information</p>
              </div>
            </div>
            <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="form-label flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              Full name *
            </label>
            <Input name="name" value={form.name} onChange={setField} required />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              Email *
            </label>
            <Input name="email" type="email" value={form.email} onChange={setField} required />
            {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              Phone
            </label>
            <Input name="phone" value={form.phone} onChange={setField} />
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-gray-500" />
              Notes
            </label>
            <Textarea name="notes" value={form.notes} onChange={setField} rows={3} />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CustomerDetailModal({ customer, onClose, onEdit }: { customer: any; onClose: () => void; onEdit: () => void }) {
  const [bookings, setBookings] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadBookings = async () => {
      try {
        const res = await bookingsAPI.list({ customer_id: customer.id })
        if (res.success) {
          setBookings(Array.isArray(res.data) ? res.data : [])
        }
      } catch (e) {
        console.error('Failed to load bookings', e)
      } finally {
        setLoading(false)
      }
    }
    loadBookings()
  }, [customer.id])

  const totalSpent = bookings.reduce((sum, b) => sum + (b.total_amount || 0), 0)

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 px-6 py-5 border-b border-gray-200 dark:border-gray-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-2xl">
                {customer.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-2xl font-bold">{customer.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{customer.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={onClose}>
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="card bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-500/10 dark:to-pink-500/10">
              <div className="card-body text-center">
                <Award className="h-6 w-6 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{customer.loyalty_points || 0}</p>
                <p className="text-xs text-purple-600 dark:text-purple-400">Loyalty Points</p>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10">
              <div className="card-body text-center">
                <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{bookings.length}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">Total Bookings</p>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/10 dark:to-emerald-500/10">
              <div className="card-body text-center">
                <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">£{(totalSpent / 100).toFixed(2)}</p>
                <p className="text-xs text-green-600 dark:text-green-400">Total Spent</p>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="card">
            <div className="card-header">
              <h4 className="font-semibold">Contact Information</h4>
            </div>
            <div className="card-body space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 dark:text-gray-400">{customer.email}</span>
              </div>
              {customer.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600 dark:text-gray-400">{customer.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  Member since {new Date(customer.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {customer.notes && (
            <div className="card">
              <div className="card-header">
                <h4 className="font-semibold">Notes</h4>
              </div>
              <div className="card-body">
                <p className="text-sm text-gray-600 dark:text-gray-400">{customer.notes}</p>
              </div>
            </div>
          )}

          {/* Booking History */}
          <div className="card">
            <div className="card-header flex items-center gap-2">
              <History className="h-5 w-5 text-orange-600" />
              <h4 className="font-semibold">Booking History</h4>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                </div>
              ) : bookings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No bookings yet
                </div>
              ) : (
                <div className="space-y-2">
                  {bookings.slice(0, 10).map(b => (
                    <div key={b.id} className="p-3 rounded-lg border border-gray-200 dark:border-gray-800 flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {new Date(b.start_time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(b.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - {new Date(b.end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">£{((b.total_amount || 0) / 100).toFixed(2)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          b.status === 'confirmed' ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300' :
                          b.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-300' :
                          'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300'
                        }`}>
                          {b.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
